import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Code, Copy, Check, Eye, Spinner, FloppyDisk, Trash, FolderOpen, PencilSimple, GlobeSimple, Bank, GlobeHemisphereEast } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_OPTIONS = [
    { value: 'apartment', label: 'Wohnung' },
    { value: 'house', label: 'Haus' },
    { value: 'land', label: 'Grundstück' },
    { value: 'office', label: 'Büro' },
    { value: 'retail', label: 'Einzelhandel' },
    { value: 'warehouse', label: 'Lager/Halle' },
    { value: 'investment', label: 'Renditeobjekt' },
    { value: 'international', label: 'Auslandsimmobilien' },
    { value: 'other', label: 'Sonstige' },
];

export default function EmbedPage() {
    const [brokers, setBrokers] = useState([]);
    const [configName, setConfigName] = useState('');
    const [config, setConfig] = useState({
        broker_id: 'all',
        property_types: [],
        marketing_type: 'all',
        max_properties: 10,
        show_filters: true,
        theme: 'light',
        search_fields: ['city', 'object_id', 'title'],
        embed_type: 'standard'
    });
    const [embedCode, setEmbedCode] = useState('');
    const [embedUrl, setEmbedUrl] = useState('');
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [savedConfigs, setSavedConfigs] = useState([]);
    const [configsLoading, setConfigsLoading] = useState(true);
    const [liveDomain, setLiveDomain] = useState('');
    const [savingDomain, setSavingDomain] = useState(false);

    const fetchConfigs = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/embed/configs`, { withCredentials: true });
            setSavedConfigs(data);
        } catch (error) {
            console.error('Error fetching embed configs:', error);
        } finally {
            setConfigsLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchBrokers = async () => {
            try {
                const { data } = await axios.get(`${API}/api/brokers`, { withCredentials: true });
                setBrokers(data);
            } catch (error) {
                console.error('Error fetching brokers:', error);
            }
        };
        const fetchSiteSettings = async () => {
            try {
                const { data } = await axios.get(`${API}/api/site-settings`, { withCredentials: true });
                if (data.live_domain) setLiveDomain(data.live_domain);
            } catch (error) {
                console.error('Error fetching site settings:', error);
            }
        };
        fetchBrokers();
        fetchConfigs();
        fetchSiteSettings();
    }, [fetchConfigs]);

    const handleSaveDomain = async () => {
        setSavingDomain(true);
        try {
            await axios.put(`${API}/api/site-settings`, { live_domain: liveDomain.trim() }, { withCredentials: true });
            toast.success('Live-Domain gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern der Domain');
        }
        setSavingDomain(false);
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const apiConfig = {
                ...config,
                broker_id: config.broker_id === 'all' ? '' : config.broker_id,
                marketing_type: config.marketing_type === 'all' ? '' : config.marketing_type,
                search_fields: config.search_fields,
                embed_type: config.embed_type || 'standard'
            };
            const { data } = await axios.post(`${API}/api/embed/generate`, apiConfig, { withCredentials: true });
            setEmbedCode(data.embed_code);
            setEmbedUrl(data.embed_url);
            toast.success('Embed Code generiert');
        } catch (error) {
            toast.error('Fehler beim Generieren');
        }
        setGenerating(false);
    };

    const handleSave = async () => {
        if (!configName.trim()) {
            toast.error('Bitte einen Namen eingeben');
            return;
        }
        setSaving(true);
        try {
            const saveData = {
                name: configName.trim(),
                broker_id: config.broker_id === 'all' ? '' : config.broker_id,
                property_types: config.property_types,
                marketing_type: config.marketing_type === 'all' ? '' : config.marketing_type,
                max_properties: config.max_properties,
                show_filters: config.show_filters,
                theme: config.theme,
                search_fields: config.search_fields,
                embed_type: config.embed_type || 'standard'
            };
            const { data } = await axios.post(`${API}/api/embed/configs`, saveData, { withCredentials: true });
            toast.success(data.status === 'updated' ? `"${configName}" aktualisiert` : `"${configName}" gespeichert`);
            fetchConfigs();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
        setSaving(false);
    };

    const handleLoadConfig = (saved) => {
        setConfigName(saved.name);
        setConfig({
            broker_id: saved.broker_id || 'all',
            property_types: saved.property_types || [],
            marketing_type: saved.marketing_type || 'all',
            max_properties: saved.max_properties || 10,
            show_filters: saved.show_filters !== false,
            theme: saved.theme || 'light',
            search_fields: Array.isArray(saved.search_fields) ? saved.search_fields : ['city', 'object_id', 'title'],
            embed_type: saved.embed_type || 'standard'
        });
        setEmbedCode('');
        setEmbedUrl('');
        toast.success(`"${saved.name}" geladen`);
    };

    const handleDeleteConfig = async (name) => {
        try {
            await axios.delete(`${API}/api/embed/configs/${encodeURIComponent(name)}`, { withCredentials: true });
            toast.success(`"${name}" gelöscht`);
            fetchConfigs();
            if (configName === name) {
                setConfigName('');
            }
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(embedCode);
        setCopied(true);
        toast.success('Code in Zwischenablage kopiert');
        setTimeout(() => setCopied(false), 2000);
    };

    const handlePropertyTypeToggle = (type) => {
        setConfig((prev) => {
            const types = prev.property_types.includes(type)
                ? prev.property_types.filter((t) => t !== type)
                : [...prev.property_types, type];
            return { ...prev, property_types: types };
        });
    };

    return (
        <div className="space-y-6" data-testid="embed-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Embed Code Generator
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Configuration Card */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Live Domain */}
                    <Card className="border" data-testid="domain-card">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                <GlobeSimple size={18} />
                                Live-Domain
                            </CardTitle>
                            <CardDescription>
                                Die Domain, unter der Ihre App live erreichbar ist. Der Embed Code wird mit dieser URL generiert.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {!liveDomain && (
                                <Alert className="mb-3 border-amber-200 bg-amber-50">
                                    <AlertDescription className="text-amber-700 text-sm">
                                        Keine Live-Domain konfiguriert. Der Embed Code verwendet die aktuelle Browser-URL.
                                    </AlertDescription>
                                </Alert>
                            )}
                            <div className="flex gap-2">
                                <Input
                                    value={liveDomain}
                                    onChange={(e) => setLiveDomain(e.target.value)}
                                    placeholder="https://www.ihre-domain.de"
                                    className="rounded-sm"
                                    data-testid="live-domain-input"
                                />
                                <Button
                                    variant="outline"
                                    className="rounded-sm shrink-0"
                                    onClick={handleSaveDomain}
                                    disabled={savingDomain}
                                    data-testid="save-domain-btn"
                                >
                                    {savingDomain ? <Spinner className="animate-spin" size={16} /> : <FloppyDisk size={16} />}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border" data-testid="config-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                                Widget Konfiguration
                            </CardTitle>
                            <CardDescription>
                                Passen Sie das Widget für Ihre Website an
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Embed-Typ Auswahl */}
                            <div className="space-y-2">
                                <Label>Embed-Typ</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    {[
                                        { value: 'standard', label: 'Standard', desc: 'Reguläre Immobilien', Icon: Code },
                                        { value: 'investment', label: 'Anlageimmobilien', desc: 'Nur Objekte mit Häkchen', Icon: Bank },
                                        { value: 'international', label: 'Immobilien International', desc: 'Land ≠ Deutschland', Icon: GlobeHemisphereEast },
                                    ].map(({ value, label, desc, Icon }) => {
                                        const active = config.embed_type === value;
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setConfig({ ...config, embed_type: value })}
                                                className={`text-left border rounded-sm p-3 transition-all hover:border-primary/60 ${
                                                    active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border'
                                                }`}
                                                data-testid={`embed-type-${value}-btn`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Icon size={16} weight={active ? 'fill' : 'regular'} className={active ? 'text-primary' : 'text-muted-foreground'} />
                                                    <span className="font-semibold text-sm">{label}</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground leading-snug">{desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {config.embed_type === 'investment' && '⚠ Anlageimmobilien werden in allen anderen Embeds und der öffentlichen Suche ausgeblendet.'}
                                    {config.embed_type === 'international' && '🌍 Zeigt Objekte mit Land ≠ Deutschland oder mit Häkchen „Auslandsimmobilie".'}
                                    {(!config.embed_type || config.embed_type === 'standard') && 'Standard zeigt alle aktiven Objekte aus Deutschland – Anlageimmobilien und Auslandsimmobilien werden ausgeblendet.'}
                                </p>
                            </div>

                            {/* Config Name */}
                            <div className="space-y-2">
                                <Label>Konfigurationsname</Label>
                                <Input
                                    value={configName}
                                    onChange={(e) => setConfigName(e.target.value)}
                                    placeholder="z.B. Homepage Widget, Landingpage Miete..."
                                    className="rounded-sm"
                                    data-testid="config-name-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Benennen Sie die Konfiguration, um sie später wiederzuverwenden
                                </p>
                            </div>

                            {/* Broker Filter */}
                            <div className="space-y-2">
                                <Label>Makler Filter</Label>
                                <Select 
                                    value={config.broker_id} 
                                    onValueChange={(value) => setConfig({ ...config, broker_id: value })}
                                >
                                    <SelectTrigger className="rounded-sm" data-testid="embed-broker-select">
                                        <SelectValue placeholder="Alle Makler" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Alle Makler</SelectItem>
                                        {brokers.map((broker) => (
                                            <SelectItem key={broker.id} value={broker.id}>
                                                {broker.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Property Types */}
                            <div className="space-y-2">
                                <Label>Immobilientypen</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {PROPERTY_TYPE_OPTIONS.map((type) => (
                                        <div key={type.value} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`type-${type.value}`}
                                                checked={config.property_types.includes(type.value)}
                                                onCheckedChange={() => handlePropertyTypeToggle(type.value)}
                                                data-testid={`type-checkbox-${type.value}`}
                                            />
                                            <Label htmlFor={`type-${type.value}`} className="text-sm font-normal cursor-pointer">
                                                {type.label}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Keine Auswahl = Alle Typen anzeigen
                                </p>
                            </div>

                            {/* Marketing Type */}
                            <div className="space-y-2">
                                <Label>Vermarktungsart</Label>
                                <Select 
                                    value={config.marketing_type} 
                                    onValueChange={(value) => setConfig({ ...config, marketing_type: value })}
                                >
                                    <SelectTrigger className="rounded-sm" data-testid="embed-marketing-select">
                                        <SelectValue placeholder="Alle" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Alle</SelectItem>
                                        <SelectItem value="sale">Nur Kauf</SelectItem>
                                        <SelectItem value="rent">Nur Miete</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Max Properties */}
                            <div className="space-y-2">
                                <Label>Max. Anzahl Immobilien</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={config.max_properties}
                                    onChange={(e) => setConfig({ ...config, max_properties: parseInt(e.target.value) || 10 })}
                                    className="rounded-sm"
                                    data-testid="embed-max-input"
                                />
                            </div>

                            {/* Show Filters */}
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Filter anzeigen</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Besucher können Immobilien filtern
                                    </p>
                                </div>
                                <Switch
                                    checked={config.show_filters}
                                    onCheckedChange={(checked) => setConfig({ ...config, show_filters: checked })}
                                    data-testid="embed-filters-switch"
                                />
                            </div>

                            {/* Search Fields */}
                            {config.show_filters && (
                                <div className="space-y-2 pl-0">
                                    <Label>Suchfelder im Widget</Label>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        Welche Felder sollen Besucher durchsuchen können?
                                    </p>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { value: 'city', label: 'Ort' },
                                            { value: 'object_id', label: 'Objekt-Nr.' },
                                            { value: 'title', label: 'Titel' },
                                        ].map((field) => (
                                            <div key={field.value} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={`sf-${field.value}`}
                                                    checked={config.search_fields.includes(field.value)}
                                                    onCheckedChange={(checked) => {
                                                        setConfig((prev) => ({
                                                            ...prev,
                                                            search_fields: checked
                                                                ? [...prev.search_fields, field.value]
                                                                : prev.search_fields.filter((f) => f !== field.value)
                                                        }));
                                                    }}
                                                    data-testid={`search-field-${field.value}`}
                                                />
                                                <Label htmlFor={`sf-${field.value}`} className="text-sm font-normal cursor-pointer">
                                                    {field.label}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Theme */}
                            <div className="space-y-2">
                                <Label>Theme</Label>
                                <Select 
                                    value={config.theme} 
                                    onValueChange={(value) => setConfig({ ...config, theme: value })}
                                >
                                    <SelectTrigger className="rounded-sm" data-testid="embed-theme-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="light">Hell</SelectItem>
                                        <SelectItem value="dark">Dunkel</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <Button 
                                    onClick={handleGenerate} 
                                    disabled={generating}
                                    className="flex-1 rounded-sm"
                                    data-testid="generate-btn"
                                >
                                    {generating ? (
                                        <>
                                            <Spinner className="animate-spin mr-2" size={16} />
                                            Generieren...
                                        </>
                                    ) : (
                                        <>
                                            <Code size={16} className="mr-2" />
                                            Code generieren
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleSave}
                                    disabled={saving || !configName.trim()}
                                    className="rounded-sm"
                                    data-testid="save-config-btn"
                                >
                                    {saving ? (
                                        <Spinner className="animate-spin mr-2" size={16} />
                                    ) : (
                                        <FloppyDisk size={16} className="mr-2" />
                                    )}
                                    Speichern
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Result Card */}
                    <Card className="border" data-testid="embed-result-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                                Embed Code
                            </CardTitle>
                            <CardDescription>
                                Kopieren Sie diesen Code in Ihre Website
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {embedCode ? (
                                <>
                                    <div className="relative">
                                        <Textarea
                                            value={embedCode}
                                            readOnly
                                            className="rounded-sm font-mono text-sm min-h-[200px] resize-none"
                                            data-testid="embed-code-textarea"
                                        />
                                        <Button
                                            size="icon"
                                            variant="outline"
                                            className="absolute top-2 right-2 rounded-sm"
                                            onClick={handleCopy}
                                            data-testid="copy-btn"
                                        >
                                            {copied ? <Check size={16} /> : <Copy size={16} />}
                                        </Button>
                                    </div>

                                    {embedUrl && (
                                        <div className="space-y-2">
                                            <Label>Vorschau-URL</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    value={embedUrl}
                                                    readOnly
                                                    className="rounded-sm font-mono text-sm"
                                                    data-testid="embed-url-input"
                                                />
                                                <Button
                                                    variant="outline"
                                                    className="rounded-sm shrink-0"
                                                    onClick={() => window.open(embedUrl, '_blank')}
                                                    data-testid="preview-btn"
                                                >
                                                    <Eye size={16} className="mr-2" />
                                                    Vorschau
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="p-4 bg-muted rounded-sm">
                                        <h4 className="font-semibold mb-2">Anleitung</h4>
                                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                                            <li>Kopieren Sie den Embed Code oben</li>
                                            <li>Fügen Sie den Code in Ihre Website ein</li>
                                            <li>Das Widget wird automatisch geladen</li>
                                        </ol>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Code size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>Klicken Sie auf "Code generieren", um den Embed Code zu erstellen</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Saved Configs Sidebar */}
                <div>
                    <Card className="border" data-testid="saved-configs-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                <FolderOpen size={20} weight="fill" />
                                Gespeicherte Konfigurationen
                            </CardTitle>
                            <CardDescription>
                                Klicken Sie auf eine Konfiguration, um sie zu laden
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {configsLoading ? (
                                <div className="flex items-center justify-center py-8 text-muted-foreground">
                                    <Spinner className="animate-spin mr-2" size={20} />
                                    Lade...
                                </div>
                            ) : savedConfigs.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground" data-testid="no-saved-configs">
                                    <FolderOpen size={36} className="mx-auto mb-3 opacity-40" />
                                    <p className="text-sm">Noch keine gespeicherten Konfigurationen</p>
                                    <p className="text-xs mt-1">Benennen und speichern Sie Ihre erste Konfiguration</p>
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="saved-configs-list">
                                    {savedConfigs.map((saved, idx) => (
                                        <div
                                            key={idx}
                                            className={`border rounded-sm p-3 transition-colors hover:bg-muted/50 ${
                                                configName === saved.name ? 'border-primary bg-primary/5' : ''
                                            }`}
                                            data-testid={`saved-config-${idx}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <button
                                                    onClick={() => handleLoadConfig(saved)}
                                                    className="text-left flex-1 min-w-0"
                                                    data-testid={`load-config-${idx}`}
                                                >
                                                    <p className="font-medium text-sm truncate">{saved.name}</p>
                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                        {saved.embed_type === 'investment' && (
                                                            <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100">
                                                                Anlage
                                                            </Badge>
                                                        )}
                                                        {saved.embed_type === 'international' && (
                                                            <Badge className="text-[10px] px-1.5 py-0 bg-sky-100 text-sky-800 border-sky-300 hover:bg-sky-100">
                                                                International
                                                            </Badge>
                                                        )}
                                                        {saved.marketing_type && (
                                                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                                {saved.marketing_type === 'sale' ? 'Kauf' : saved.marketing_type === 'rent' ? 'Miete' : 'Alle'}
                                                            </Badge>
                                                        )}
                                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                            Max {saved.max_properties}
                                                        </Badge>
                                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                            {saved.theme === 'dark' ? 'Dunkel' : 'Hell'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-[10px] text-muted-foreground mt-1.5">
                                                        {saved.created_at ? new Date(saved.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                                    </p>
                                                </button>
                                                <div className="flex gap-1 shrink-0">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 rounded-sm"
                                                        onClick={() => handleLoadConfig(saved)}
                                                        title="Laden"
                                                        data-testid={`edit-config-${idx}`}
                                                    >
                                                        <PencilSimple size={14} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 rounded-sm text-destructive hover:text-destructive"
                                                        onClick={() => handleDeleteConfig(saved.name)}
                                                        title="Löschen"
                                                        data-testid={`delete-config-${idx}`}
                                                    >
                                                        <Trash size={14} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
